import React from "react"
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"

import type { CompanyConnectorCredentialGrantWarning } from "~/ui-rtk/api/types/company-connector.types"

/**
 * Amber chip for a connector whose public metadata carries the API's
 * `credentialGrantWarning`: an Instagram Insights reconnect for another
 * company narrowed the shared Meta login's asset selection and this
 * connector's Instagram account fell out of it. The connector still reads as
 * CONNECTED / healthy, so the chip is the only sign — and a reconnect
 * (ticking this account in Meta's dialog) is the only fix. Rendered on the
 * connectors table card, in the connection dialog and on every row of the
 * multi-connection list so the warning is visible wherever the connector is.
 */
export const CREDENTIAL_GRANT_WARNING_LABEL =
  "Meta authorisation no longer covers this account — reconnect"

/** Tooltip for the chip: when it was checked and what is missing. The account id and a day only — never a credential, never another company's id. */
export function describeCredentialGrantWarning(
  warning: CompanyConnectorCredentialGrantWarning,
): string {
  const day = warning.checkedAt.slice(0, 10)
  return `Checked ${day}: a later login by the same Meta person (a reconnect for another company, or the agency discovery login) narrowed this Meta login's asset selection, and Instagram account ${warning.missingIgUserId} is no longer in it. Reconnect and tick this account in Meta's dialog.`
}

type CredentialGrantWarningChipProps = {
  warning: CompanyConnectorCredentialGrantWarning
  /** Let the label wrap (dialog widths); the table card keeps it on one line. */
  wrap?: boolean
  className?: string
}

export const CredentialGrantWarningChip: React.FC<
  CredentialGrantWarningChipProps
> = ({ warning, wrap = false, className }) => (
  <div
    role="status"
    data-testid="credential-grant-warning"
    title={describeCredentialGrantWarning(warning)}
    className={`flex items-center gap-1 px-2 py-1 bg-system-yellow-500 ${
      wrap ? "w-fit max-w-full rounded-md" : "rounded-full"
    } ${className ?? ""}`}
  >
    <ExclamationTriangleIcon className="w-3 h-3 shrink-0 text-black" />
    <span
      className={`text-xs font-medium text-black ${
        wrap ? "" : "whitespace-nowrap"
      }`}
    >
      {CREDENTIAL_GRANT_WARNING_LABEL}
    </span>
  </div>
)
